import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { FinancialValue } from '../shared/FinancialValue';
import { formatCurrency } from '../../utils/financialFormat';
import type { DossierItem, DossierHole, BiMethodSupportRow, BiMethodSupportStatus } from '../../types/methodology';
import { MethodDossierSection } from '../methodology/MethodDossierSection';

export interface MethodElectionCardProps {
  method: {
    method_id: string;
    label: string;
    posture: string;
    gross?: number | null;
    value: number | null;
    net?: number | null;
    total_policy_deductions?: number | null;
    authority: string;
    assumptions: string[];
    notes?: string | null;
    attested: boolean;
    attested_by?: string | null;
    carrier_challenges?: string[];
    is_elected?: boolean;
    strengths?: DossierItem[];
    weaknesses?: DossierItem[];
    holes?: DossierHole[];
  };
  /** Server-side support status for this method (bi_method_support row). Absent → the server could
   *  not assess support, so NO election is offered. */
  support?: BiMethodSupportRow | null;
  isElected: boolean;
  onElect: () => void;
  electing: boolean;
}

/** Status chips use hierarchy / weight / iconography only (verification tokens, not the brand accent):
 *  supportable = verified stamp; everything else needs a human or is blocked. */
const STATUS_LABEL: Record<BiMethodSupportStatus, string> = {
  supportable: 'Supportable',
  not_supportable: 'Not supportable',
  documents_incomplete: 'Documents incomplete',
  not_computed: 'Not computed',
  disqualified: 'Disqualified',
  not_assessed: 'Not assessed',
  not_applicable: 'Not applicable',
  coverage_line: 'Coverage line',
};

export const MethodElectionCard: React.FC<MethodElectionCardProps> = ({
  method,
  support,
  isElected,
  onElect,
  electing,
}) => {
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [showChallenges, setShowChallenges] = useState(false);

  const validMethodIds = ['bi_bottom_up', 'bi_reconciled', 'bi_top_down', 'bi_projection_trend', 'bi_two_period'];
  if (!validMethodIds.includes(method.method_id)) {
    return null;
  }

  // Electable ONLY when the server's support status says so — never inferred from a figure.
  const canElect = support?.electable === true;
  const grossVal = support ? support.figure.gross : (method.gross ?? method.value);
  const netVal = support ? support.figure.net : (method.net ?? method.value);
  const deductions = method.total_policy_deductions ?? 0;
  const challenges = method.carrier_challenges ?? [];
  const showAllChallenges = challenges.length <= 3;
  const visibleChallenges = showAllChallenges ? challenges : challenges.slice(0, 2);

  const postureLabel = method.posture
    ? method.posture.charAt(0).toUpperCase() + method.posture.slice(1).toLowerCase()
    : '';

  return (
    <div className="relative flex flex-col h-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] overflow-hidden">
      {isElected && (
        <div className="h-0.5 w-full bg-[var(--color-brand)] transition-colors duration-150" />
      )}

      <div className="p-5 flex flex-col h-full">
        <div className="flex justify-between items-center h-4 mb-3">
          <span className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[var(--color-text-muted)]">
            {postureLabel}
          </span>
          {isElected && (
            <span className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[var(--color-text-primary)]">
              ELECTED
            </span>
          )}
        </div>

        <h3 className="text-[15px] font-semibold text-[var(--color-text-primary)] line-clamp-2 min-h-[2.5rem] mb-4">
          {method.label}
        </h3>

        <div className="space-y-3 mb-4">
          <div className="flex justify-between items-baseline">
            <span className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
              GROSS BI LOSS
            </span>
            <FinancialValue value={grossVal} size="lg" />
          </div>

          <div className="flex justify-between items-baseline">
            <span className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
              NET OF POLICY DEDUCTIONS
            </span>
            {netVal != null ? (
              <FinancialValue value={netVal} size="md" />
            ) : (
              <span className="text-[12px] text-[var(--color-text-muted)]" data-testid="net-not-netted">
                Not netted
              </span>
            )}
          </div>

          {netVal != null && (
            <div className="text-right">
              <span className="text-[11px] text-[var(--color-text-muted)] font-mono">
                ({deductions > 0 ? formatCurrency(deductions, { cents: true }) : '$0.00'} deducted)
              </span>
            </div>
          )}
        </div>

        {support && (
          <div className="mb-4" data-testid={`support-${support.method}`}>
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[11px] uppercase tracking-[0.08em] font-semibold ${
                  support.status === 'supportable'
                    ? 'border-[var(--color-border)] text-[var(--color-text-secondary)]'
                    : 'border-[var(--color-blocker-tint-bd)] bg-[var(--color-blocker-tint)] text-[var(--color-text-primary)]'
                }`}
              >
                {support.status === 'supportable' ? <Check size={11} aria-hidden /> : <span aria-hidden>!</span>}
                {STATUS_LABEL[support.status] ?? support.status}
              </span>
              {support.stale && (
                <span className="text-[11px] text-[var(--color-needs-human-text)]">Stale engine — re-run</span>
              )}
            </div>
            {support.reasons.length > 0 && (
              <ul className="space-y-1">
                {support.reasons.map((r, i) => (
                  <li key={i} className="text-[12px] text-[var(--color-text-muted)] leading-snug">{r}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {!support && (
          <p className="mb-4 text-[12px] text-[var(--color-text-muted)]" data-testid="support-unavailable">
            Support status unavailable — election is not offered until it can be assessed.
          </p>
        )}

        <div className="mt-auto pt-4 border-t border-[var(--color-border)]">
          <div className="min-h-[24px] mb-3">
            {method.attested ? (
              <div className="inline-flex items-center gap-2 px-2 py-1 border border-[var(--color-border)] bg-[var(--color-bg-surface)] rounded-sm">
                <span className="text-[11px]">✓</span>
                <span className="text-[11px] text-[var(--color-text-secondary)]">
                  Attested · {method.attested_by || 'Unknown'}
                </span>
              </div>
            ) : (
              <span className="text-[11px] text-[var(--color-text-muted)]">
                Not yet attested — set on the Accountant surface
              </span>
            )}
          </div>

          <div className="flex justify-end items-center min-h-[2.75rem]">
            {isElected ? (
              <div className="text-right">
                <div className="text-[11px] text-[var(--color-text-secondary)]">
                  Currently elected — the sworn PoL, demand, and court bundle foot to this figure.
                </div>
              </div>
            ) : (
              <button
                onClick={onElect}
                disabled={electing || !canElect}
                className="h-8 px-4 bg-[var(--color-brand)] text-[var(--color-on-brand)] text-[13px] font-semibold rounded-sm hover:bg-[var(--color-brand-hover)] transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)] disabled:bg-transparent disabled:text-[var(--color-text-muted)] disabled:border disabled:border-[var(--color-border)] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {electing ? 'Electing…' : 'Elect this method'}
              </button>
            )}
          </div>
        </div>

        <div className="pt-4 mt-4 border-t border-[var(--color-border)]">
          <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] mb-2">
            WHY IT HOLDS
          </div>
          <p className="text-[13px] text-[var(--color-text-secondary)] line-clamp-2 leading-relaxed">
            {method.authority}
          </p>
        </div>

        <div className="pt-4 mt-4 border-t border-[var(--color-border)]">
          <button
            onClick={() => setShowAssumptions(!showAssumptions)}
            aria-expanded={showAssumptions}
            className="flex items-center gap-2 text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors duration-150"
          >
            <span>{showAssumptions ? '▾' : '▸'}</span>
            <span>Assumptions &amp; inputs ({method.assumptions.length})</span>
          </button>

          {showAssumptions && (
            <div className="mt-3 space-y-2">
              {method.assumptions.map((assumption, idx) => (
                <div key={idx} className="flex gap-3">
                  <span className="text-[12px] font-mono text-[var(--color-text-muted)] w-6 shrink-0">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[12px] text-[var(--color-text-secondary)]">{assumption}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-4 mt-4 border-t border-[var(--color-border)]">
          <button
            onClick={() => setShowChallenges(!showChallenges)}
            aria-expanded={showChallenges}
            className="flex items-center gap-2 text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors duration-150"
          >
            <span>{showChallenges ? '▾' : '▸'}</span>
            <span>HOW THE CARRIER FIGHTS IT · {challenges.length}</span>
          </button>

          {showChallenges && (
            <div className="mt-3 space-y-3">
              {challenges.length === 0 ? (
                <p className="text-[12px] text-[var(--color-text-muted)]">
                  No method-specific challenges in the defense run.
                </p>
              ) : (
                <>
                  {visibleChallenges.map((challenge, idx) => (
                    <div key={idx} className="flex gap-3">
                      <span className="text-[12px] font-mono text-[var(--color-text-muted)] w-6 shrink-0">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1 text-[13px] text-[var(--color-text-secondary)] line-clamp-2">
                        {challenge}
                      </span>
                      {/* Reserved slot for the future Answer + Exhibit affordance (increment 2c). */}
                      <div className="w-16 shrink-0" />
                    </div>
                  ))}
                  {!showAllChallenges && (
                    <div className="text-[11px] text-[var(--color-text-muted)] pl-9">
                      All {challenges.length} challenges
                    </div>
                  )}
                </>
              )}
              {method.notes && (
                <div className="text-[12px] text-[var(--color-text-muted)] pl-9 mt-2">
                  Note — {method.notes}
                </div>
              )}
            </div>
          )}
        </div>

        <MethodDossierSection
          strengths={method.strengths}
          weaknesses={method.weaknesses}
          holes={method.holes}
        />
      </div>
    </div>
  );
};

export default MethodElectionCard;
